import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { Canvas, useFrame } from '@react-three/fiber';
import { RoundedBox, ContactShadows } from '@react-three/drei';
import * as THREE from 'three';
import { useTranslation } from 'react-i18next';

import styles from './RollingDiceWidget.module.css';

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const checkWebglSupport = () => {
  if (typeof document === 'undefined') {
    return false;
  }
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    return Boolean(gl);
  } catch (error) {
    return false;
  }
};

const PinIcon = ({ filled = false }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 4h6" />
    <path d="M4 20l7-7" />
    <path d="M10 14l-2 2" />
    <path d="M9 3l6 6" />
    <path d="M15 9l4 4-2 2-4-4" />
    {filled ? <path d="M15 9l4 4-2 2-4-4Z" /> : null}
  </svg>
);

// Up=1, Down=6, Front=2, Back=5, Right=3, Left=4
const FACE_TO_TOP_QUAT = (value, yawRad) => {
  const qBase = new THREE.Quaternion();
  const e = new THREE.Euler(0, 0, 0, 'XYZ');

  switch (value) {
    case 1:
      e.set(0, 0, 0);
      break;
    case 6:
      e.set(Math.PI, 0, 0);
      break;
    case 2:
      e.set(-Math.PI / 2, 0, 0);
      break;
    case 5:
      e.set(Math.PI / 2, 0, 0);
      break;
    case 3:
      e.set(0, 0, Math.PI / 2);
      break;
    case 4:
      e.set(0, 0, -Math.PI / 2);
      break;
    default:
      e.set(0, 0, 0);
  }

  qBase.setFromEuler(e);

  const qYaw = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yawRad);
  return qYaw.multiply(qBase);
};

const PIP_POINTS = (n) => {
  const p = 0.23;
  switch (n) {
    case 1:
      return [[0, 0]];
    case 2:
      return [
        [-p, p],
        [p, -p],
      ];
    case 3:
      return [
        [-p, p],
        [0, 0],
        [p, -p],
      ];
    case 4:
      return [
        [-p, p],
        [p, p],
        [-p, -p],
        [p, -p],
      ];
    case 5:
      return [
        [-p, p],
        [p, p],
        [0, 0],
        [-p, -p],
        [p, -p],
      ];
    case 6:
      return [
        [-p, p],
        [-p, 0],
        [-p, -p],
        [p, p],
        [p, 0],
        [p, -p],
      ];
    default:
      return [[0, 0]];
  }
};

function makePipsTexture(num, size = 512) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  ctx.clearRect(0, 0, size, size);

  const pts = PIP_POINTS(num);
  const r = Math.floor(size * 0.07);
  const cx = size / 2;
  const cy = size / 2;
  const step = size * 0.28;

  for (const [x, y] of pts) {
    const px = cx + x * (step / 0.23);
    const py = cy + y * (step / 0.23);

    ctx.fillStyle = '#0b0b0b';
    ctx.beginPath();
    ctx.arc(px, py, r, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fill();
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 16;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;

  return tex;
}

function FacePipsPaint({ number, position, rotation }) {
  const tex = useMemo(() => makePipsTexture(number, 512), [number]);

  return (
    <mesh position={position} rotation={rotation} renderOrder={10}>
      <planeGeometry args={[0.90, 0.90]} />
      <meshBasicMaterial
        map={tex}
        transparent
        alphaTest={0.25}
        side={THREE.DoubleSide}
        depthTest
        depthWrite={true}
        toneMapped={false}
        polygonOffset
        polygonOffsetFactor={-2}
        polygonOffsetUnits={-2}
      />
    </mesh>
  );
}

const Dice3D = forwardRef(function Dice3D({ initialValue = 1, onSettled }, ref) {
  const cubeRef = useRef(null);

  const rollRef = useRef({
    active: false,
    start: 0,
    duration: 1200,
    startQuat: new THREE.Quaternion(),
    targetQuat: new THREE.Quaternion(),
    axis1: new THREE.Vector3(1, 0, 0),
    axis2: new THREE.Vector3(0, 1, 0),
    spin1: 0,
    spin2: 0,
    result: 1,
    split: 0.86,
    holdQuat: null,
  });

  useEffect(() => {
    if (!cubeRef.current) return;
    const q = FACE_TO_TOP_QUAT(clamp(initialValue, 1, 6), 0);
    cubeRef.current.quaternion.copy(q).normalize();
  }, [initialValue]);

  useImperativeHandle(
    ref,
    () => ({
      rollTo: ({ value, durationMs, turnsMin, turnsMax }) => {
        if (!cubeRef.current) return;

        const v = clamp(Number(value), 1, 6);
        const d = Math.max(150, Number(durationMs) || 1200);

        const tMin = clamp(Number(turnsMin ?? 1), 0, 6);
        const tMax = clamp(Number(turnsMax ?? 2), tMin, 10);

        const yaw = Math.random() * Math.PI * 2;
        const target = FACE_TO_TOP_QUAT(v, yaw);

        const axis1 = new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.1, Math.random() - 0.5).normalize();
        const axis2 = new THREE.Vector3(Math.random() - 0.5, Math.random() + 0.2, Math.random() - 0.5).normalize();

        const turns1 = randomInt(tMin, tMax);
        const turns2 = randomInt(tMin, tMax);

        const spin1 = turns1 * Math.PI * 2;
        const spin2 = turns2 * Math.PI * 2 * 0.75;

        rollRef.current = {
          ...rollRef.current,
          active: true,
          start: performance.now(),
          duration: d,
          startQuat: cubeRef.current.quaternion.clone(),
          targetQuat: target,
          axis1,
          axis2,
          spin1,
          spin2,
          result: v,
          holdQuat: null,
        };
      },
    }),
    [],
  );

  useFrame(() => {
    if (!cubeRef.current) return;

    const r = rollRef.current;
    if (!r.active) return;

    const now = performance.now();
    const t = clamp((now - r.start) / r.duration, 0, 1);

    const lift = Math.sin(Math.PI * t) * 0.18;
    const bounce = Math.sin(4 * Math.PI * t) * Math.exp(-6 * t) * 0.03;
    cubeRef.current.position.y = lift + bounce;

    const split = r.split;

    if (t < split) {
      const tn = clamp(t / split, 0, 1);
      const spinFade = 1 - tn;

      const qSpin1 = new THREE.Quaternion().setFromAxisAngle(r.axis1, r.spin1 * spinFade);
      const qSpin2 = new THREE.Quaternion().setFromAxisAngle(r.axis2, r.spin2 * spinFade);

      const base = r.startQuat.clone().multiply(qSpin1).multiply(qSpin2);
      const k = easeOutCubic(tn) * 0.35;
      const current = base.slerp(r.targetQuat, k).normalize();

      cubeRef.current.quaternion.copy(current);

      if (tn >= 0.999 && !r.holdQuat) r.holdQuat = current.clone();
    } else {
      if (!r.holdQuat) r.holdQuat = cubeRef.current.quaternion.clone();

      const tn = clamp((t - split) / (1 - split), 0, 1);
      const k = easeOutCubic(tn);

      const current = r.holdQuat.clone().slerp(r.targetQuat, k).normalize();
      cubeRef.current.quaternion.copy(current);
    }

    if (t >= 1) {
      cubeRef.current.quaternion.copy(r.targetQuat).normalize();
      cubeRef.current.position.y = 0;

      r.active = false;
      onSettled?.(r.result);
    }
  });

  const e = 0.512;

  return (
    <group>
      <ambientLight intensity={0.72} />
      <hemisphereLight intensity={0.28} color="#fff3cc" groundColor="#5b6470" />
      <directionalLight position={[4.2, 7.4, 3.6]} intensity={1.6} castShadow shadow-mapSize-width={2048} shadow-mapSize-height={2048} />
      <directionalLight position={[-3.4, 2.4, -4.4]} intensity={1.1} />
      <pointLight position={[2.6, 1.8, -3.6]} intensity={0.55} color="#ffe2a8" />
      <pointLight position={[-1.6, 3.2, 2.8]} intensity={0.4} />

      <group ref={cubeRef}>
        <RoundedBox args={[1, 1, 1]} radius={0.18} smoothness={8} castShadow receiveShadow>
          <meshStandardMaterial color="#f6d77c" roughness={0.22} metalness={0.88} />
        </RoundedBox>

        <FacePipsPaint number={2} position={[0, 0, e]} rotation={[0, 0, 0]} />
        <FacePipsPaint number={5} position={[0, 0, -e]} rotation={[0, Math.PI, 0]} />
        <FacePipsPaint number={3} position={[e, 0, 0]} rotation={[0, -Math.PI / 2, 0]} />
        <FacePipsPaint number={4} position={[-e, 0, 0]} rotation={[0, Math.PI / 2, 0]} />
        <FacePipsPaint number={1} position={[0, e, 0]} rotation={[-Math.PI / 2, 0, 0]} />
        <FacePipsPaint number={6} position={[0, -e, 0]} rotation={[Math.PI / 2, 0, 0]} />
      </group>

      <ContactShadows position={[0, -0.62, 0]} opacity={0.55} scale={3.2} blur={2.6} far={2} />
    </group>
  );
});

const RollingDiceWidget = forwardRef(function RollingDiceWidget(
  {
    initialValue = 1,
    initialPosition = { x: 60, y: 120 },
    size = 160,
    rollDurationMs = 1200,
    spinTurnsMin = 1,
    spinTurnsMax = 2,
    disabled = false,
    onRollRequest,
    onRollAnimationStart,
    onRollAnimationEnd,
    onSettled,
    overlay,
    className,
  },
  ref,
) {
  const [displayValue, setDisplayValue] = useState(initialValue);
  const [isRolling, setIsRolling] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isPinned, setIsPinned] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [webglSupported, setWebglSupported] = useState(true);
  const [canvasKey, setCanvasKey] = useState(0);
  const [canvasElement, setCanvasElement] = useState(null);
  const { t } = useTranslation('game');

  const dice3dRef = useRef(null);
  const rootRef = useRef(null);
  const pendingRollValueRef = useRef(null);
  const shouldRestoreCanvasRef = useRef(false);

  const [pos, setPos] = useState(() => ({
    x: initialPosition?.x ?? 60,
    y: initialPosition?.y ?? 120,
  }));

  const dragRef = useRef({
    dragging: false,
    pointerId: null,
    startPointerX: 0,
    startPointerY: 0,
    startX: 0,
    startY: 0,
  });

  const canInteract = !disabled && !isRolling && !isRequesting;

  const finishInterruptedRoll = useCallback(
    (reason) => {
      const pendingValue = pendingRollValueRef.current;
      if (pendingValue === null || pendingValue === undefined) {
        return;
      }

      pendingRollValueRef.current = null;
      setIsRolling(false);
      setDisplayValue(pendingValue);
      onRollAnimationEnd?.({ value: pendingValue, resultShown: true, interrupted: true, reason });
      onSettled?.(pendingValue);
    },
    [onRollAnimationEnd, onSettled],
  );

  const remountCanvas = useCallback(
    (reason) => {
      finishInterruptedRoll(reason);
      setCanvasKey((prev) => prev + 1);
    },
    [finishInterruptedRoll],
  );

  useEffect(() => {
    setWebglSupported(checkWebglSupport());
  }, []);

  useEffect(() => {
    if (!canvasElement) {
      return undefined;
    }

    const handleContextLoss = (event) => {
      event.preventDefault();
      shouldRestoreCanvasRef.current = true;
      setWebglSupported(false);
      remountCanvas('context-lost');
    };

    const handleContextRestored = () => {
      shouldRestoreCanvasRef.current = false;
      setWebglSupported(checkWebglSupport());
      remountCanvas('context-restored');
    };

    canvasElement.addEventListener('webglcontextlost', handleContextLoss, false);
    canvasElement.addEventListener('webglcontextrestored', handleContextRestored, false);

    return () => {
      canvasElement.removeEventListener('webglcontextlost', handleContextLoss);
      canvasElement.removeEventListener('webglcontextrestored', handleContextRestored);
    };
  }, [canvasElement, remountCanvas]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        shouldRestoreCanvasRef.current = true;
        return;
      }
      if (shouldRestoreCanvasRef.current) {
        shouldRestoreCanvasRef.current = false;
        setWebglSupported(checkWebglSupport());
        remountCanvas('visibility-return');
      }
    };

    const handleBlur = () => {
      shouldRestoreCanvasRef.current = true;
    };

    const handleFocus = () => {
      if (shouldRestoreCanvasRef.current) {
        shouldRestoreCanvasRef.current = false;
        setWebglSupported(checkWebglSupport());
        remountCanvas('window-focus');
      }
    };

    const handlePageShow = () => {
      setWebglSupported(checkWebglSupport());
      remountCanvas('page-show');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [remountCanvas]);

  useEffect(() => {
    if (isRolling || isRequesting) return;
    setDisplayValue(clamp(initialValue, 1, 6));
  }, [initialValue, isRequesting, isRolling]);

  useEffect(() => {
    if (!webglSupported) {
      setCanvasElement(null);
    }
  }, [webglSupported]);

  const rollTo = useCallback(
    (nextValue) => {
      if (isRolling) return;

      const v = clamp(Number(nextValue ?? randomInt(1, 6)), 1, 6);

      pendingRollValueRef.current = v;
      setIsRolling(true);
      onRollAnimationStart?.(v);

      if (!dice3dRef.current) {
        setIsRolling(false);
        pendingRollValueRef.current = null;
        setDisplayValue(v);
        onRollAnimationEnd?.({ value: v, resultShown: true });
        onSettled?.(v);
        return;
      }

      dice3dRef.current?.rollTo({
        value: v,
        durationMs: rollDurationMs,
        turnsMin: spinTurnsMin,
        turnsMax: spinTurnsMax,
      });
    },
    [isRolling, onRollAnimationEnd, onRollAnimationStart, onSettled, rollDurationMs, spinTurnsMax, spinTurnsMin],
  );

  useImperativeHandle(
    ref,
    () => ({
      rollTo,
      getValue: () => displayValue,
      isPinned: () => isPinned,
    }),
    [displayValue, isPinned, rollTo],
  );

  const handleTogglePinned = useCallback(() => {
    setIsPinned((prev) => {
      const next = !prev;
      if (!next && rootRef.current) {
        const rect = rootRef.current.getBoundingClientRect();
        setPos({ x: rect.left, y: rect.top });
      }
      if (next) {
        setIsDragging(false);
      }
      return next;
    });
  }, []);

  const onHandlePointerDown = useCallback(
    (e) => {
      if (isPinned) return;

      setIsDragging(true);
      dragRef.current.dragging = true;
      dragRef.current.pointerId = e.pointerId;
      dragRef.current.startPointerX = e.clientX;
      dragRef.current.startPointerY = e.clientY;
      dragRef.current.startX = pos.x;
      dragRef.current.startY = pos.y;

      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [isPinned, pos.x, pos.y],
  );

  const onHandlePointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    if (d.pointerId !== e.pointerId) return;

    const dx = e.clientX - d.startPointerX;
    const dy = e.clientY - d.startPointerY;

    setPos({ x: d.startX + dx, y: d.startY + dy });
  }, []);

  const endDrag = useCallback((e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    if (d.pointerId !== e.pointerId) return;

    d.dragging = false;
    d.pointerId = null;
    setIsDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  }, []);

  const handleRollRequest = useCallback(async () => {
    if (!canInteract) return;
    if (!onRollRequest) {
      rollTo(undefined);
      return;
    }

    setIsRequesting(true);
    try {
      const nextValue = await onRollRequest();
      if (nextValue === null || nextValue === undefined) {
        return;
      }
      rollTo(nextValue);
    } finally {
      setIsRequesting(false);
    }
  }, [canInteract, onRollRequest, rollTo]);

  const wrapperStyle = useMemo(
    () => (isPinned ? undefined : { transform: `translate(${pos.x}px, ${pos.y}px)` }),
    [isPinned, pos.x, pos.y],
  );

  const rootClassName = [
    styles.widgetRoot,
    !isPinned ? styles.widgetFloating : '',
    isDragging ? styles.widgetDragging : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <div
      ref={rootRef}
      className={rootClassName}
      style={{ ...wrapperStyle, '--dice-size': `${size}px` }}
    >
      <div className={styles.frame}>
        {!isPinned && (
          <div
            className={styles.dragHandle}
            onPointerDown={onHandlePointerDown}
            onPointerMove={onHandlePointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            aria-label="Move dice"
          >
            {t('room.roll.dragHint')}
          </div>
        )}
        <button
          type="button"
          className={[styles.pinButton, isPinned ? styles.pinButtonActive : ''].join(' ')}
          onClick={handleTogglePinned}
          aria-label={isPinned ? 'Unpin dice' : 'Pin dice'}
          title={isPinned ? 'Открепить' : 'Закрепить'}
        >
          <PinIcon filled={isPinned} />
        </button>
        <div className={styles.centerWrap}>
          <div className={styles.diceContainer}>
            {webglSupported ? (
              <Canvas
                key={canvasKey}
                className={styles.canvas}
                shadows
                dpr={[1, 2]}
                gl={{ antialias: true, alpha: true }}
                camera={{ position: [1.55, 2.55, 1.75], fov: 32, near: 0.1, far: 100 }}
                onCreated={({ gl, camera }) => {
                  gl.setClearColor(0x000000, 0);
                  camera.lookAt(0, 0.05, 0);
                  setCanvasElement(gl.domElement);
                }}
                onPointerDown={handleRollRequest}
              >
                <Dice3D
                  ref={dice3dRef}
                  initialValue={initialValue}
                  onSettled={(v) => {
                    setIsRolling(false);
                    setDisplayValue(v);
                    onRollAnimationEnd?.({ value: v, resultShown: true });
                    pendingRollValueRef.current = null;
                    onSettled?.(v);
                  }}
                />
              </Canvas>
            ) : (
              <button
                type="button"
                className={styles.diceFallback}
                onClick={handleRollRequest}
                disabled={!canInteract}
                aria-label={t('room.roll.button')}
              >
                {displayValue}
              </button>
            )}
          </div>
        </div>
        {overlay && (
          <div className={styles.overlay}>
            <div className={styles.overlayInner}>{overlay}</div>
          </div>
        )}
      </div>
    </div>
  );

  if (!isPinned && typeof document !== 'undefined') {
    return createPortal(content, document.body);
  }

  return content;
});

export default RollingDiceWidget;
